import { memo, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Stack, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import useResponse from '@/hooks/useResponse';
import {
  FILE_SEARCH_LIST_VARIABLES,
  useFileSearch,
} from '@/modules/tasks/features/actionItems/hooks/useFileSearch';
import useUpdateActionItem from '@/modules/tasks/features/actionItems/hooks/useUpdateActionItem';

import useFileActions from '../../../hooks/useFileActions';
import StacksList from '../DuplicateTask/components/TaskLocationSelector/StacksList';
import { DialogProps } from '../types';

const dialogSize = css`
  max-width: 52rem;
  height: 72rem;
`;

function MoveTaskToList({ onClose, task }: DialogProps) {
  const { state } = useFileActions({ onSuccess: onClose });
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');

  const [selectedListId, setSelectedListId] = useState<string | null>(task?.fileId ?? null);

  const { files: lists, isFetching: isFetchingLists } = useFileSearch({
    filters: { ...FILE_SEARCH_LIST_VARIABLES, fileIds: [selectedListId ?? ''] },
    pause: !selectedListId,
  });

  const currentSelectedList = useMemo(() => {
    return lists?.find((list) => list.id === task?.fileId);
  }, [lists, task?.fileId]);

  const { updateActionItem } = useUpdateActionItem();

  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const onSubmit = useCallback(async () => {
    if (!selectedListId || !task || !('id' in task)) return;

    const selectedList = lists?.find((list) => list.id === selectedListId);
    setIsFetching(true);

    const response = await updateActionItem({
      data: {
        actionItemId: task.id,
        fileId: selectedListId,
        toUnsetFields: {
          stageIds: true,
        },
      },
    });

    handleResponse({
      condition: !response.error,
      onSuccess: () => {
        showSnackbar({
          message: t('Task:TaskWasMovedTo', { name: selectedList?.name }),
        });
        onClose();
      },
    });

    setIsFetching(false);
  }, [handleResponse, onClose, lists, selectedListId, showSnackbar, t, task, updateActionItem]);

  return (
    <Modal
      action={{
        disabled:
          isFetching ||
          state.isFetching ||
          isFetchingLists ||
          !selectedListId ||
          selectedListId === currentSelectedList?.id,
        isSubmitting: isFetching || state.isFetching || isFetchingLists,
        text: t('Common:Actions.Move'),
        onSubmit: onSubmit,
      }}
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      header={{
        title: t('Task:MoveToListDialog.Title'),
        description: t('Task:MoveToListDialog.Description'),
      }}
      onClose={onClose}>
      <Stack className="mx-24 mb-24 flex-1" direction="col">
        <StacksList
          showRadio
          search={search}
          selectItem={setSelectedListId}
          selectedFileId={selectedListId}
          setSearch={setSearch}
        />
      </Stack>
    </Modal>
  );
}

export default memo(MoveTaskToList);
